import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "@/lib/apiClient";
import type { Pc } from "@/types/pc";
import type { CurrentUser, User } from "@/types/user";
import type { Reservation } from "@/types/reservation";
import { LoadingState } from "@/components/LoadingState";
import { EmptyState } from "@/components/EmptyState";
import { ReservationModal } from "@/components/ReservationModal";

export function ReservationDetailPage({ currentUser }: { currentUser: CurrentUser }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState<Reservation | null>(null);
  const [pcs, setPcs] = useState<Pc[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const canProxy = currentUser.role === "manager" || currentUser.role === "admin";

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    Promise.all([
      api.get<Reservation>(`/reservations/${id}`),
      api.get<Pc[]>("/pcs"),
      canProxy ? api.get<User[]>("/users") : Promise.resolve<User[]>([]),
    ])
      .then(([r, p, u]) => {
        setReservation(r);
        setPcs(p);
        setUsers(u);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "予約が見つかりません。"))
      .finally(() => setLoading(false));
  }, [id, canProxy]);

  if (loading) return <LoadingState />;
  if (error || !reservation) return <EmptyState message={error ?? "予約が見つかりません。"} />;

  const pcName = pcs.find((p) => p.id === reservation.pc_id)?.name ?? `PC#${reservation.pc_id}`;
  const userName =
    reservation.user_id === currentUser.id
      ? currentUser.displayName
      : users.find((u) => u.id === reservation.user_id)?.displayName ?? `利用者#${reservation.user_id}`;

  return (
    <ReservationModal
      reservation={reservation}
      pcName={pcName}
      userName={userName}
      currentUser={currentUser}
      onClose={() => navigate("/my-reservations")}
      onChanged={(updated) => setReservation(updated)}
    />
  );
}
